import { useCallback, useRef } from 'react';
import { useSlotAssetGeneratorStore } from '../store/slotAssetGeneratorStore';
import {
  generateSlotAsset,
  rerollSingleImage,
  saveResultToProject,
} from '../services/slot-asset-generator';
import { MODEL_PROVIDER_KEY_MAP } from '../../core/types/image-generation.types';
import { resolveApiKey } from '../utils/api-keys';
import { shouldUseProxy } from '../services/ai-proxy';
import type { SlotAssetGenerationRequest, SlotGenerationHistoryEntry } from '../../core/types/slot-asset-generator.types';

const generateId = () => Math.random().toString(36).substring(2, 11);

export function useSlotAssetGenerator() {
  const abortRef = useRef<AbortController | null>(null);

  const selectedComponentId = useSlotAssetGeneratorStore((s) => s.selectedComponentId);
  const components = useSlotAssetGeneratorStore((s) => s.components);
  const generation = useSlotAssetGeneratorStore((s) => s.generation);

  const startGeneration = useSlotAssetGeneratorStore((s) => s.startGeneration);
  const setGenerationResult = useSlotAssetGeneratorStore((s) => s.setGenerationResult);
  const updateGenerationProgress = useSlotAssetGeneratorStore((s) => s.updateGenerationProgress);
  const setGenerationError = useSlotAssetGeneratorStore((s) => s.setGenerationError);
  const cancelGeneration = useSlotAssetGeneratorStore((s) => s.cancelGeneration);
  const startReroll = useSlotAssetGeneratorStore((s) => s.startReroll);
  const markImageSaved = useSlotAssetGeneratorStore((s) => s.markImageSaved);
  const addToast = useSlotAssetGeneratorStore((s) => s.addToast);
  const addHistoryEntry = useSlotAssetGeneratorStore((s) => s.addHistoryEntry);

  // Track the current generation's request for history recording
  const pendingHistoryRef = useRef<{ request: SlotAssetGenerationRequest; savedPaths: string[] } | null>(null);

  const selectedComponent = selectedComponentId
    ? components.find((c) => c.id === selectedComponentId) ?? null
    : null;

  /**
   * Start generating images for the selected component.
   */
  const generate = useCallback(async (request: SlotAssetGenerationRequest) => {
    // Abort previous if any
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Pre-validate API key before starting generation (checks store then env var)
    // Skip validation for Google when backend proxy is available
    const requiredProvider = MODEL_PROVIDER_KEY_MAP[request.model];
    if (requiredProvider !== null && !(requiredProvider === 'google' && shouldUseProxy())) {
      const storeKey = useSlotAssetGeneratorStore.getState().apiKeys[requiredProvider] ?? '';
      const key = resolveApiKey(requiredProvider, storeKey);
      if (!key) {
        setGenerationError(
          `API key required for ${request.model}. Click the \u2699 gear icon to configure your ${requiredProvider} API key.`
        );
        return;
      }
    }

    startGeneration(request);
    pendingHistoryRef.current = { request, savedPaths: [] };

    try {
      let completed = 0;
      for await (const { index, result } of generateSlotAsset(request, controller.signal)) {
        setGenerationResult(index, result);
        completed++;
        updateGenerationProgress(completed, request.imageCount);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Cancellation handled by cancelGeneration
        return;
      }
      setGenerationError(err instanceof Error ? err.message : 'Generation failed');
    }
  }, [startGeneration, setGenerationResult, updateGenerationProgress, setGenerationError]);

  /**
   * Cancel in-progress generation.
   */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    cancelGeneration();
  }, [cancelGeneration]);

  /**
   * Re-roll a single image.
   */
  const reroll = useCallback(async (resultId: string) => {
    const request = generation.request;
    if (!request) return;

    startReroll(resultId);

    const controller = new AbortController();
    try {
      const newResult = await rerollSingleImage(request, controller.signal);
      // Find the index of this result and update it
      const index = useSlotAssetGeneratorStore.getState().generation.results.findIndex(
        (r) => r.id === resultId
      );
      if (index !== -1) {
        setGenerationResult(index, { ...newResult, id: resultId });
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        const index = useSlotAssetGeneratorStore.getState().generation.results.findIndex(
          (r) => r.id === resultId
        );
        if (index !== -1) {
          setGenerationResult(index, {
            id: resultId,
            imageUrl: '',
            status: 'error',
            error: err instanceof Error ? err.message : 'Re-roll failed',
          });
        }
      }
    }
  }, [generation.request, startReroll, setGenerationResult]);

  /**
   * Save a result image to the project.
   * Uses getState() to avoid stale closure over generation results.
   */
  const saveImage = useCallback(async (resultId: string) => {
    // Read fresh state at invocation time -- not from render closure
    const store = useSlotAssetGeneratorStore.getState();
    const result = store.generation.results.find((r) => r.id === resultId);
    const component = store.selectedComponentId
      ? store.components.find((c) => c.id === store.selectedComponentId)
      : null;

    if (!result || !component) return;

    try {
      const savedPath = await saveResultToProject(result, component);
      markImageSaved(resultId, savedPath);

      // Track for history
      if (pendingHistoryRef.current) {
        pendingHistoryRef.current.savedPaths.push(savedPath);
      }

      // Record history entry per save
      const req = store.generation.request;
      if (req) {
        const entry: SlotGenerationHistoryEntry = {
          id: generateId(),
          componentId: component.id,
          componentName: component.name,
          prompt: req.prompt,
          model: req.model,
          style: req.style,
          quality: req.quality,
          imageCount: 1,
          savedPaths: [savedPath],
          timestamp: Date.now(),
        };
        addHistoryEntry(entry);
      }

      // Show success toast
      addToast({
        id: generateId(),
        savedPath,
        componentName: component.name,
        timestamp: Date.now(),
      });
    } catch (err) {
      console.warn('[SlotAssetGenerator] Save failed:', err);
    }
  }, [markImageSaved, addToast, addHistoryEntry]);

  return {
    generate,
    cancel,
    reroll,
    saveImage,
    isGenerating: generation.status === 'generating',
    progress: generation.progress,
    results: generation.results,
    error: generation.error,
    selectedComponent,
  };
}
